// Procedural clips: walking, paddling and swimming.
import * as THREE from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { PI } from '../../lib/math.js';
import { WALK_CYCLE } from '../walking.js';
import { FINGERS } from './loader.js';
import { wq } from './retarget.js';

export let AX, AY, LEG_K, ARM_K, makeWalk, makeClip, paddleFrame, swimFrame;

export function buildAnimations() {
  makeWalk = function makeWalk(root, idle) {
    const tgt = SkeletonUtils.clone(root),
      bones = {};
    tgt.traverse((o) => {
      if (o.isBone) bones[o.name] = o;
    });
    const mx = new THREE.AnimationMixer(tgt);
    mx.clipAction(idle).play();
    mx.update(0);
    const base = {};
    for (const n in bones) base[n] = bones[n].quaternion.clone();
    const RQ = wq(tgt),
      rot = (n, axis, ang) => {
        const b = bones[THREE.PropertyBinding.sanitizeNodeName(n)];
        if (!b) return;
        const pq = wq(b.parent),
          q = new THREE.Quaternion().setFromAxisAngle(axis.clone().applyQuaternion(RQ), ang);
        b.quaternion.premultiply(pq.clone().invert().multiply(q).multiply(pq));
      };
    const N = 33,
      names = Object.keys(bones).filter((n) => !FINGERS.test(n)),
      times = new Float32Array(N),
      vals = names.map(() => new Float32Array(N * 4));
    for (let i = 0; i < N; i++) {
      const f = (i / (N - 1)) * PI * 2;
      times[i] = (i / (N - 1)) * WALK_CYCLE;
      for (const n in bones) bones[n].quaternion.copy(base[n]);
      rot('Hips', AY, 0.08 * Math.sin(f));
      rot('Torso', AY, -0.13 * Math.sin(f));
      rot('Torso', AX, 0.05);
      rot('Head', AX, -0.05);
      for (const [s, off] of [
        ['L', 0],
        ['R', PI],
      ]) {
        const sw = Math.sin(f + off),
          c = Math.cos(f + off),
          knee = 0.06 + 0.95 * Math.max(0, c) ** 1.6 + 0.12 * Math.max(0, sw) * Math.max(0, -c);
        // the foot's pitch in the world: flat while planted, heel first on contact, rolling onto the toes at push-off,
        // slightly raised through the swing; the ankle cancels whatever the thigh and knee add
        const pitch =
          c < 0
            ? -0.2 * Math.max(0, sw) ** 3 + 0.5 * Math.max(0, -sw) ** 3
            : 0.5 * Math.max(0, -sw) ** 2 - 0.1 - 0.1 * Math.max(0, sw);
        rot('UpperLeg.' + s, AX, -0.4 * sw);
        rot('LowerLeg.' + s, AX, knee);
        rot('Foot.' + s, AX, pitch + 0.4 * sw - knee);
        rot('UpperArm.' + s, AX, 0.1 + 0.3 * sw);
        rot('LowerArm.' + s, AX, -0.04 - 0.3 * Math.max(0, -sw));
      }
      names.forEach((n, k) => bones[n].quaternion.toArray(vals[k], i * 4));
    }
    mx.stopAllAction();
    mx.uncacheRoot(tgt);
    return new THREE.AnimationClip(
      'WalkCycle',
      WALK_CYCLE,
      names.map((n, k) => new THREE.QuaternionKeyframeTrack(n + '.quaternion', times, vals[k])),
    );
  };
  // A procedural clip over one cycle: frame(rot, phase) poses bones (axes in the model's frame) on top of a base pose,
  // either a clip's first frame or the bind pose. Used for swimming.
  makeClip = function makeClip(name, root, baseClip, dur, frame) {
    const tgt = SkeletonUtils.clone(root),
      bones = {};
    tgt.traverse((o) => {
      if (o.isBone) bones[o.name] = o;
    });
    const mx = baseClip && new THREE.AnimationMixer(tgt);
    if (mx) {
      mx.clipAction(baseClip).play();
      mx.update(0);
    }
    const base = {};
    for (const n in bones) base[n] = bones[n].quaternion.clone();
    const RQ = wq(tgt),
      rot = (n, axis, ang) => {
        const b = bones[THREE.PropertyBinding.sanitizeNodeName(n)];
        if (!b) return;
        const pq = wq(b.parent),
          q = new THREE.Quaternion().setFromAxisAngle(axis.clone().applyQuaternion(RQ), ang);
        b.quaternion.premultiply(pq.clone().invert().multiply(q).multiply(pq));
      };
    const N = 33,
      names = Object.keys(bones).filter((n) => !FINGERS.test(n)),
      times = new Float32Array(N),
      vals = names.map(() => new Float32Array(N * 4));
    for (let i = 0; i < N; i++) {
      times[i] = (i / (N - 1)) * dur;
      for (const n in bones) bones[n].quaternion.copy(base[n]);
      frame(rot, (i / (N - 1)) * PI * 2);
      names.forEach((n, k) => bones[n].quaternion.toArray(vals[k], i * 4));
    }
    if (mx) {
      mx.stopAllAction();
      mx.uncacheRoot(tgt);
    }
    return new THREE.AnimationClip(
      name,
      dur,
      names.map((n, k) => new THREE.QuaternionKeyframeTrack(n + '.quaternion', times, vals[k])),
    );
  };
  // The kit's Walk is a crouched combat walk, so the walk cycle is built here on top of the standing idle pose:
  // thighs swing, knees fold mid-swing, ankles roll heel to toe, arms swing against the legs, hips and chest counter-twist.
  // One cycle (two steps) lasts WALK_CYCLE seconds and covers about 0.7 units.
  AX = new THREE.Vector3(1, 0, 0);
  AY = new THREE.Vector3(0, 1, 0);
  LEG_K = 0.88;
  ARM_K = 0.92;
  // stand-up paddling on top of the idle pose: knees soft, torso bending into each stroke, arms reaching down one side
  // then the other (two strokes per cycle)
  paddleFrame = (rot, f) => {
    const st = Math.sin(f),
      side = Math.sign(Math.sin(f / 2 + 0.001)) || 1;
    rot('Torso', AX, 0.25 + 0.2 * Math.max(0, st));
    rot('Torso', AY, 0.25 * side);
    for (const s of ['L', 'R']) {
      rot('UpperLeg.' + s, AX, -0.15);
      rot('LowerLeg.' + s, AX, 0.3);
    } // the arms are solved onto the paddle each frame
  };
  // breaststroke from the T-pose: arms sweep out and fold back in, frog kick half a cycle later, body nearly flat
  const AZ = new THREE.Vector3(0, 0, 1);
  swimFrame = (rot, f) => {
    rot('Torso', AX, 0.95);
    rot('Head', AX, -0.9);
    for (const [s, sg] of [
      ['L', 1],
      ['R', -1],
    ]) {
      const pull = Math.max(0, Math.sin(f)),
        kick = Math.max(0, Math.sin(f + PI));
      rot('UpperArm.' + s, AY, -sg * (0.75 + 0.5 * Math.cos(f)));
      rot('UpperArm.' + s, AZ, -sg * (0.25 + 0.15 * pull));
      rot('LowerArm.' + s, AY, -sg * 1.1 * pull);
      rot('UpperLeg.' + s, AX, 0.3 + 0.45 * kick);
      rot('UpperLeg.' + s, AY, sg * (0.12 + 0.3 * kick));
      rot('LowerLeg.' + s, AX, 0.15 + 1.1 * kick);
    }
  };
}
